import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, ShoppingBag, ArrowUpRight, Minus, Plus } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function QtyStepper({ qty, setQty }) {
  return (
    <div className="flex items-center border border-zinc-800">
      <button
        aria-label="Decrease quantity"
        onClick={() => setQty(Math.max(1, qty - 1))}
        className="w-9 h-9 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
      >
        <Minus size={13} />
      </button>
      <span className="w-8 text-center font-mono text-sm text-white tabular-nums">{qty}</span>
      <button
        aria-label="Increase quantity"
        onClick={() => setQty(Math.min(10, qty + 1))}
        className="w-9 h-9 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
      >
        <Plus size={13} />
      </button>
    </div>
  );
}

function ProductCard({ product, index }) {
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(false);
  const [imgOk, setImgOk] = useState(true);
  const variants = product.variants || [];
  const hasSizes = variants.length > 1;
  const [selected, setSelected] = useState(null);

  const buy = async () => {
    if (hasSizes && !selected) {
      toast.message("Pick a size first");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        lookup_key: product.lookup_key,
        quantity: qty,
        origin_url: window.location.origin,
        size: selected?.size || null,
        sync_variant_id: selected?.printful_sync_variant_id || null,
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        throw new Error("no url");
      }
    } catch (e) {
      toast.error("Couldn't start checkout. Try again.");
      setLoading(false);
    }
  };

  return (
    <motion.div
      data-testid={`merch-card-${product.lookup_key}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className="group border border-zinc-800 flex flex-col"
    >
      <div className="relative aspect-square overflow-hidden border-b border-zinc-900 bg-black">
        {imgOk ? (
          <>
            <div
              className="absolute inset-0"
              style={{ background: "radial-gradient(circle at 50% 40%, rgba(255,255,255,0.20), rgba(255,255,255,0.06) 46%, transparent 72%)" }}
            />
            <img
              src={product.image}
              alt={product.name}
              loading="lazy"
              draggable={false}
              onError={() => setImgOk(false)}
              className={`absolute inset-0 w-full h-full object-contain p-4 transition-all duration-700 group-hover:scale-105 ${
                product.image_hover ? "group-hover:opacity-0" : ""
              }`}
            />
            {product.image_hover && (
              <img
                src={product.image_hover}
                alt={`${product.name} — back`}
                loading="lazy"
                draggable={false}
                className="absolute inset-0 w-full h-full object-contain p-4 opacity-0 scale-105 transition-all duration-700 group-hover:opacity-100"
              />
            )}
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-4 bg-black">
            <span className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white">
              FRE3BRD
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              {product.lookup_key === "merch_mug" ? "Ceramic Mug" : "Oversized Tee"}
            </span>
          </div>
        )}
      </div>
      <div className="p-5 flex flex-col gap-4 flex-1">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display font-bold text-lg md:text-xl tracking-tight text-white leading-tight">
            {product.name}
          </h3>
          <span className="font-mono text-sm text-white shrink-0">
            ${product.price.toFixed(2)}
          </span>
        </div>
        <div className="mt-auto flex items-center gap-3">
          {hasSizes && (
            <div className="w-full">
              <div className="flex flex-wrap gap-2">
                {variants.map((v) => (
                  <button
                    key={v.printful_sync_variant_id}
                    data-testid={`size-${product.lookup_key}-${v.size}`}
                    onClick={() => setSelected(v)}
                    className={`min-w-9 h-9 px-2 border font-mono text-[11px] uppercase transition-colors ${
                      selected?.printful_sync_variant_id === v.printful_sync_variant_id
                        ? "border-white bg-white text-black"
                        : "border-zinc-700 text-zinc-300 hover:border-white"
                    }`}
                  >
                    {v.size || "One"}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <QtyStepper qty={qty} setQty={setQty} />
          <button
            data-testid={`merch-buy-${product.lookup_key}`}
            onClick={buy}
            disabled={loading}
            className="flex-1 bg-white text-black py-3 font-mono uppercase tracking-widest text-[11px] hover:bg-zinc-200 transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <ShoppingBag size={14} />}
            {loading ? "Loading" : "Buy Now"}
          </button>
        </div>
        <a
          href={product.printful_url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white transition-colors inline-flex items-center gap-1"
        >
          View on store <ArrowUpRight size={11} />
        </a>
      </div>
    </motion.div>
  );
}

export default function Merch() {
  const [products, setProducts] = useState([]);
  const [cashapp, setCashapp] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/merch`)
      .then((res) => {
        setProducts(res.data.products || []);
        setCashapp(res.data.cashapp || "");
      })
      .catch(() => {});
  }, []);

  if (!products.length) return null;

  return (
    <section
      data-testid="merch-section"
      className="relative w-full px-5 md:px-10 py-20 md:py-28 border-t border-zinc-900"
    >
      <div className="max-w-4xl mx-auto w-full">
        <div className="mb-10 md:mb-14">
          <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-zinc-500 mb-3">
            Rep the movement
          </p>
          <h2 className="font-display font-black text-3xl md:text-5xl tracking-tighter text-white">
            Merch
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
          {products.map((p, i) => (
            <ProductCard key={p.lookup_key} product={p} index={i} />
          ))}
        </div>

        {cashapp && (
          <motion.div
            data-testid="cashapp-block"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-6 border border-zinc-800 p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5"
          >
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
                Support the artist
              </p>
              <h3 className="font-display font-bold text-xl md:text-2xl tracking-tight text-white">
                Tip the tape on Cash App
              </h3>
              <p className="font-body text-sm text-zinc-400 mt-1">
                Free means free — but if the tape moved you, drop something in the hat.
              </p>
            </div>
            <a
              data-testid="cashapp-link"
              href={cashapp}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 flex items-center gap-3 border border-zinc-700 text-white px-6 py-3.5 font-mono uppercase tracking-widest text-xs hover:border-white hover:bg-white hover:text-black transition-all"
            >
              <i className="fa-brands fa-square-cash-app text-lg" />
              Donate via Cash App
            </a>
          </motion.div>
        )}
      </div>
    </section>
  );
}
